import { bound } from "@frui.ts/helpers";
import { watchBusy } from "@frui.ts/screens";
import { addError, attachManualValidator, getValidationMessage, removeError, validateAll } from "@frui.ts/validation";
import { action, computed, observable, reaction } from "mobx";
import { ExemptionState } from "models/orderItem";
import ValidChargeResponse from "models/validChargeResponse";
import ChargeManagementService from "services/chargeManagementService";
import ChargeRegistrationService, { RequestSource } from "services/chargeRegistrationService";
import EventBus, {
  emailUpdated,
  putChargeContactFailure,
  putChargeContactSuccess,
  putChargeValidityFailure,
  putChargeValiditySuccess,
  vehicleHasValidCharges,
  vehicleRegistration,
} from "services/eventBus";
import LocalizationService from "services/localizationService";
import AppRouter from "services/appRouter";
import SignalRNotificationService from "services/signalR/notification/signalRNotificationService";
import { dateToIsoString, endOfCzechDay, isSameOrBeforePragueDay, maxValidSince, zonedCZTimeToUtc } from "utils/dateUtils";
import ChargeManagementEditPartViewModel from "./chargeManagementEditPartViewModel";
import ChargeManagementEditViewModel from "./chargeManagementEditViewModel";
import EmailInputViewModel from "./common/emailInputViewModel";
import TurnstileViewModel from "../../common/turnstileViewModel";
import React from "react";
import * as Sentry from "@sentry/react";
import { scrollToFirstError } from "utils/scrollTo";
import EnumService from "services/enumService";
import { startOfCzechDay, utcToCZZonedTime } from "../../../utils/dateUtils";
import ChargeValidityRuleResponse from "entities/chargeValidityRuleResponse";
import ChargeValidityInstructionsResponse from "entities/chargeValidityInstructionsResponse";
import ChargeOverlapType from "entities/chargeOverlapType";
import InputEmail from "entities/InputEmail";
import ChargeValidity from "entities/chargeValidity";
import ChargeTypesService from "services/chargeTypesService";
import TimeService from "services/timeService";
import { TurnstileInstance } from "@marsidev/react-turnstile";

export default class ChargeManagementValidityViewModel extends ChargeManagementEditPartViewModel {
  private sinceRegex = /%since%/g;
  private untilRegex = /%until%/g;
  private intervalId?: any;
  readonly IDLE_TIMER_TIMEOUT = 100 * 60;
  readonly IDLE_PROMPT_TIMEOUT = 100 * 60 * 4;
  readonly IDLE_TIMER_DEBOUNCE = 250;
  @observable private _validCharges?: ValidChargeResponse[];
  @observable private _exemptionState?: ExemptionState;
  @observable emailInputViewModel?: EmailInputViewModel;
  turnstileViewModel: TurnstileViewModel;
  @observable turnstileToken: string;
  @observable chargeValidityRules: ChargeValidityRuleResponse[];
  @observable postponedValidityRule: ChargeValidityRuleResponse | undefined;
  @observable counter: number;
  @observable isFormFilled = false;
  @observable isSingleCharTypeToday?: boolean;
  @observable overlappingCharges?: any;

  constructor(
    public localizationService: LocalizationService,
    protected chargeManagementService: ChargeManagementService,
    protected timeService: TimeService,
    protected chargeRegistrationService: ChargeRegistrationService,
    protected chargeTypesService: ChargeTypesService,
    protected enumService: EnumService,
    protected eventBus: EventBus,
    protected signalRNotificationService: SignalRNotificationService,
    private emailInputViewModelFactory: ReturnType<typeof EmailInputViewModel.Factory>,
    private router: AppRouter,
    turnstileViewModelFactory: ReturnType<typeof TurnstileViewModel.Factory>
  ) {
    super();
    this.navigationName = "validity";
    this.turnstileViewModel = turnstileViewModelFactory("chargeManagementValidity", true);
    this.unsubscriptions.push(
      this.eventBus.subscribe(putChargeValidityFailure, this.putChargeValidityFailure),
      this.eventBus.subscribe(putChargeValiditySuccess, this.putChargeValiditySuccess),
      this.eventBus.subscribe(vehicleRegistration, this.vehicleIsExempted),
      this.eventBus.subscribe(vehicleHasValidCharges, this.vehicleHasValidCharges),
      this.eventBus.subscribe(putChargeContactFailure, this.putChargeContactFailure),
      this.eventBus.subscribe(putChargeContactSuccess, this.putChargeContactSuccess),
      this.eventBus.subscribe(emailUpdated, this.onEmailUpdated)
    );
  }

  private ta = (code: string) => this.localizationService.translateAttribute("orderItem", code);

  protected async onInitialize(): Promise<Promise<any> | void> {
    this.chargeValidityRules = await this.enumService.lazyValues("charge_validity_rules");
    await this.timeService.getCurrentServerTime();
    this.getPostponedValidityMaxDays(this.timeService.currentServerTime, this.chargeValidityRules);
    if (this.charge && !this.charge.emailAddress && this.charge) {
      this.emailInputViewModel = this.emailInputViewModelFactory(
        new InputEmail(this.charge.emailAddress, this.charge.notificationPreferences._emailConfirmation),
        "",
        ""
      );
    }

    attachManualValidator(this);
    this.unsubscriptions.push(
      reaction(
        () => this.newValidity?.validSince,
        () => {
          this.validSinceCheck();
        }
      ),
      reaction(
        () => this.postponedValidityRule?.postponedValidityMaxDays,
        () => {
          this.validSinceCheck();
        }
      )
    );
    return super.onInitialize();
  }

  @bound
  protected putChargeValiditySuccess(data: ReturnType<typeof putChargeValiditySuccess>) {
    this.clearAfterAsyncAction();

    if (!this.charge) return;

    this.success = true;
    this.charge._canBeChanged = false;
    if (data.payload.validity.validSince) {
      this.charge.validSince = data.payload.validity.validSince;
      // When we change validSince to today's date, only the vehicleChangeBeforeValidity will be allowed.
      // We must set it manually to display during validity change form.
      this.charge.permittedActions.vehicleChangeDuringValidity.isAllowed = true;
    }
    if (data.payload.validity.validUntil) this.charge.validUntil = data.payload.validity.validUntil;
    if (data.payload.validity.authCode) this.charge.authCode = data.payload.validity.authCode;
    if (data.payload.validity.emailAddress)
      this.charge.emailAddress = this.charge.notificationPreferences._emailConfirmation = data.payload.validity.emailAddress;

    this.router.navigate(ChargeManagementEditViewModel, undefined, {
      countryId: this.charge.vehicle.countryId,
      licensePlate: this.charge.vehicle.licensePlate,
      authCode: data.payload.validity.authCode,
    });
  }

  @bound
  protected putChargeValidityFailure(data: ReturnType<typeof putChargeValidityFailure>) {
    this.clearAfterAsyncAction();
    this.setErrorMessageBasedOnType(data.payload.type);
  }

  @bound
  protected onEmailUpdated(data: ReturnType<typeof emailUpdated>) {
    this.setEmailAddress(data.payload.emailAddress);
    if (this.emailInputViewModel) {
      this.emailInputViewModel = undefined;
    }
  }

  // TODO: R-29-2 Remove after user accepted.
  @bound
  protected async putChargeContactSuccess(data: ReturnType<typeof putChargeContactSuccess>) {
    if (data.payload.triggerer !== "validityChange" && data.payload.triggerer !== "contactChange") {
      this.emailInputViewModel = undefined;
      return;
    } else {
      this.emailInputViewModel = this.emailInputViewModelFactory(
        new InputEmail(this.charge?.emailAddress, this.charge?.notificationPreferences._emailConfirmation),
        "",
        ""
      );
    }

    this.clearAfterAsyncAction();

    if (!this.charge) {
      return;
    }
    this.charge._canBeChanged = false;
    this.charge.notificationPreferences.smsNotificationEnabled = data.payload.notificationPreferences?.smsNotificationEnabled;
    this.charge.notificationPreferences.emailNotificationEnabled = data.payload.notificationPreferences?.emailNotificationEnabled;
    this.charge.emailAddress = data.payload.notificationPreferences?.emailAddress;
    this.charge.notificationPreferences._emailAddressNotification = data.payload.notificationPreferences?.emailAddress;
    this.charge.notificationPreferences._emailConfirmation = data.payload.notificationPreferences?.emailAddress;
    this.charge.notificationPreferences.phoneNumber = data.payload.notificationPreferences?.phoneNumber;
    this.charge.authCode = data.payload?.authCode ?? "";

    this.router.navigate(ChargeManagementEditViewModel, undefined, {
      countryId: this.charge.vehicle.countryId,
      licensePlate: this.charge.vehicle.licensePlate,
      authCode: data.payload.authCode,
    });

    // const newValidSince = this.newValidity?.validSince;

    // if (!newValidSince) return;

    // await this.chargeManagementService.editChargeValidity(newValidSince, this.turnstileToken);
  }

  @action.bound
  protected putChargeContactFailure(data: ReturnType<typeof putChargeContactFailure>) {
    if (data.payload.triggerer !== "validityChange") {
      return;
    }

    this.clearAfterAsyncAction();
    this.setErrorMessageBasedOnType(data.payload.type);
  }

  @computed
  get validSince() {
    if (!this.charge || !this.charge.validSince) return "-";

    const validSince = this.charge.validSince;

    return validSince ? this.localizationService.formatDateTime(new Date(validSince)) : "-";
  }

  @computed
  get isDifferentTimeZone() {
    return this.timeService.isFetched && !this.timeService.isServerTimeSame;
  }

  @computed
  get serverTime() {
    return this.timeService.currentServerTime;
  }

  @computed
  get validUntil() {
    if (!this.charge || !this.charge.validUntil) return "-";

    const validUntil = this.charge.validUntil;

    return validUntil ? this.localizationService.formatDateTime(new Date(validUntil)) : "-";
  }

  @computed
  get newValidity() {
    return {
      validSince: this.charge?.newValidSince,
      validUntil: this.charge?.newValidUntil,
    };
  }

  @computed
  get validChargesInfo() {
    if (this._validCharges) {
      return this._validCharges
        .map(validCharge =>
          this.ta("validSinceRegistrationInfo")
            .replace(this.untilRegex, this.localizationService.formatDateTime(validCharge.validUntil))
            .replace(this.sinceRegex, this.localizationService.formatDateTime(validCharge.validSince))
        )
        .join("\r\n\r\n");
    }

    return undefined;
  }

  @computed
  get exemptionInfo() {
    switch (this._exemptionState) {
      case ExemptionState.Exempted:
        return this.ta("exemptedRegistrationInfo");
      default:
        return undefined;
    }
  }

  @action.bound
  async handleIdle() {
    this.counter = this.IDLE_PROMPT_TIMEOUT;
    this.intervalId = setInterval(async () => {
      this.counter = this.counter - 1000;
      if (this.counter === 0 && this.newValidity?.validSince) {
        this.chargeValidityRules = await this.enumService.lazyValues("charge_validity_rules");
        this.getPostponedValidityMaxDays(this.timeService.currentServerTime, this.chargeValidityRules);
      }
      if (this.counter === 0) {
        this.clearTimeoutInterval();
        this.handleIdle();
      }
    }, 1000);
  }

  @bound
  clearTimeoutInterval() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = undefined;
    }
  }

  @action.bound
  singleCharTypeToday() {
    if (!this.charge) return;
    const chargeType = this.chargeTypesService.getPriceListItemChargeType(this.charge._chargeTypeId, true);

    if (!chargeType) return;

    this.isSingleCharTypeToday = this.charge?.newValidSince
      ? isSameOrBeforePragueDay(this.charge.newValidSince, this.timeService.currentServerTime) &&
        this.chargeTypesService.findChargeType(chargeType.id)?.validFor === ChargeValidity.SingleDay
      : false;
  }

  @action.bound
  getPostponedValidityMaxDays(validSince: Date | undefined, chargeValidityRules: ChargeValidityRuleResponse[]) {
    if (validSince && chargeValidityRules) {
      const filteredChargeValidityRules = chargeValidityRules.find(
        rule =>
          validSince &&
          rule.validSinceDate &&
          startOfCzechDay(new Date(rule.validSinceDate)) <= validSince &&
          (!rule.validUntilDate || startOfCzechDay(new Date(rule.validUntilDate)) >= validSince)
      );
      this.postponedValidityRule = filteredChargeValidityRules ?? undefined;
    }
  }

  @bound
  async validSinceCheck() {
    this.isFormFilled = this.newValidity.validSince !== undefined;
    this.resetSecondStep();
    this.singleCharTypeToday();
    const max = dateToIsoString(
      maxValidSince(utcToCZZonedTime(this.charge?.chargeOrderConfirmedAt!), this.postponedValidityRule?.postponedValidityMaxDays)
    );
    removeError(this, "charge");
    if (
      this.charge?.validSince &&
      this.charge?.newValidSince &&
      dateToIsoString(utcToCZZonedTime(this.charge?.validSince)) === dateToIsoString(utcToCZZonedTime(this.charge?.newValidSince))
    ) {
      addError(this, "charge", this.translate("validators.sameValidSince"));
    } else if (
      this.charge?.validSince &&
      this.charge?.newValidSince &&
      this.postponedValidityRule?.postponedValidityMaxDays &&
      max > dateToIsoString(utcToCZZonedTime(this.charge?.validSince)) &&
      max < dateToIsoString(utcToCZZonedTime(this.charge?.newValidSince))
    ) {
      const d1 = new Date(max);
      const d2 = new Date(dateToIsoString(this.timeService.currentServerTime));

      if (isNaN(d1.getTime()) || isNaN(d2.getTime())) {
        return;
      }
      const msNaDen = 1000 * 60 * 60 * 24;
      const diff = Math.abs((d2.getTime() - d1.getTime()) / msNaDen);
      addError(this, "charge", this.localizationService.translateAttribute("validSince", "max").replace("%amount%", `${diff}`));
    } else if (
      this.charge?.validSince &&
      this.charge?.newValidSince &&
      this.postponedValidityRule?.postponedValidityMaxDays &&
      dateToIsoString(this.timeService.currentServerTime) > dateToIsoString(utcToCZZonedTime(this.charge.newValidSince))
    ) {
      addError(this, "charge", this.localizationService.translateAttribute("validSince", "min"));
    }
  }

  @action.bound
  vehicleIsExempted(data: ReturnType<typeof vehicleRegistration>) {
    const payload = data.payload;
    if (payload.source === RequestSource.NewValidity && payload.vehicleRegistration.isGivenExemption) {
      this._exemptionState = ExemptionState.Exempted;
    }
  }

  @action.bound
  vehicleHasValidCharges(data: ReturnType<typeof vehicleHasValidCharges>) {
    if (!data.payload.source || !data.payload.validCharges.charges.length) return;

    if (data.payload.source === RequestSource.NewValidity) {
      // Filter out current charge
      this._validCharges = data.payload.validCharges.charges.filter(
        charge =>
          !(
            this.charge &&
            this.charge &&
            this.charge.validSince &&
            charge.validSince.toISOString() === this.charge.validSince.toISOString() &&
            charge.validUntil.toISOString() === this.charge?.validUntil.toISOString()
          )
      );
    }
  }

  @action.bound
  onNewValidityChanged() {
    this.isFormFilled = this.newValidity.validSince !== undefined;
    this.resetSecondStep();
  }

  @action.bound
  @watchBusy
  async submit(turnstileToken?: string) {
    if (!this.validate()) {
      scrollToFirstError(this.formElement);
      return;
    }

    if (!this.showSecondStep) {
      await this.checkRsvValidity();
      this.showSecondStep = true;
    } else {
      if (!turnstileToken || !this.turnstileViewModel.turnstileToken) return;
      const token = turnstileToken || this.turnstileViewModel.turnstileToken;

      const newValidSince = this.newValidity?.validSince;

      if (!newValidSince) return;

      this.isLoading = true;

      try {
        if (this.charge && !this.charge.emailAddress && this.charge) {
          await this.chargeManagementService.editChargeValidity(newValidSince, this.emailInputViewModel?.inputEmail.email, token);
          return;
        }

        await this.chargeManagementService.editChargeValidity(newValidSince, undefined, token);
      } finally {
        this.isLoading = false;
      }
    }
  }

  @action.bound
  onCancelSecondStep() {
    if (!this.charge) {
      return;
    }
    this.charge.newValidSince = undefined!;
    this.showSecondStep = false;
    this.hideConfirmChangeButton = false;
  }

  @bound
  protected async checkRsvValidity() {
    this.success = false;
    this.errorMessage = undefined;

    if (!this.validate()) {
      scrollToFirstError(this.formElement);
      return false;
    }

    const countryId = this.charge?.vehicle.countryId;
    const licensePlate = this.charge?.vehicle.licensePlate;
    const newValidSince = this.newValidity?.validSince;
    const chargeId = this.charge?.id;

    if (!countryId || !licensePlate || !newValidSince || !chargeId) return false;

    this.secondStepLoading = true;
    const response = await this.chargeManagementService.getChargeValidityInstructions(
      countryId,
      licensePlate,
      newValidSince,
      chargeId
    );
    this.secondStepLoading = false;

    let isSuccessful = false;
    if (response instanceof ChargeValidityInstructionsResponse) {
      this.overlappingCharges = response.overlappingCharges?.map(overlap =>
        this.translateOverlappingCharge(overlap, this.isDifferentTimeZone ?? false)
      );

      if (response.overlapTypes?.includes(ChargeOverlapType.ExemptionOverlap)) {
        const exemptionOverlapMessage = this.translateOverlapReason(ChargeOverlapType.ExemptionOverlap);
        this.warningMessages = [exemptionOverlapMessage];
      }

      isSuccessful = this.warningMessages.length === 0 && this.overlappingCharges.length === 0;
    }

    if (isSuccessful) {
      this.hideConfirmChangeButton = true;
    } else {
      this.showSecondStep = true;
    }
    return isSuccessful;
  }

  @bound
  public validate() {
    const objectsForValidation: any[] = [this.charge?.newValidSince, this.charge?.newValidUntil, this];

    if (this.charge && !this.charge.emailAddress) {
      objectsForValidation.push(this.emailInputViewModel?.inputEmail);
    }

    return validateAll(objectsForValidation);
  }

  @computed
  get sameValidSinceError() {
    return getValidationMessage(this, "charge");
  }

  @computed
  get hasSecondStep() {
    return true;
  }

  @computed
  get canSubmit(): boolean {
    return this.isFormFilled;
  }

  get canEdit(): boolean {
    if (!this.charge) {
      return false;
    }
    return this.charge.permittedActions.validityChange.isAllowed;
  }
}
